import { useState } from 'react';
import { 
  Box, 
  Button, 
  TextField, 
  Typography, 
  CircularProgress,
  Alert,
  Fade,
  Paper
} from '@mui/material';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import NotificationsIcon from '@mui/icons-material/Notifications';
import ReactGA from 'react-ga4';
import { useStyles } from '../../styles/styles';

const CityNotificationForm = ({ cityName }) => {
  const { classes } = useStyles();
  
  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    email: '',
    subject: `Book Tour Notification - ${cityName}`, // Hidden field with city name
    message: '', // Will be "N/A" if empty
    receiveUpdates: false
  });
  
  const [loading, setLoading] = useState(false);
  const [status, setStatus] = useState({ type: '', message: '' });
  const [isSubmitSuccess, setIsSubmitSuccess] = useState(false);
  
  const handleChange = (e) => {
    const { name, value, checked, type } = e.target;
    setFormData({
      ...formData,
      [name]: type === 'checkbox' ? checked : value
    });
  };
  
  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setStatus({ type: '', message: '' });
    
    // Prepare form data with N/A for empty message
    const submitData = {
      ...formData,
      message: formData.message.trim() || 'N/A'
    };
    
    try {
      const response = await fetch('https://xnmboqwiuc.execute-api.us-east-1.amazonaws.com/ContactForm', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(submitData),
      });
      
      const data = await response.json();
      
      if (response.ok) {
        // Track successful form submission
        try {
          ReactGA.event('generate_lead', {
            event_category: 'conversion',
            item_name: 'city_notification_form',
            custom_parameters: {
              city: cityName
            }
          });
        } catch (trackingError) {
          console.warn('Analytics tracking failed:', trackingError);
        }
        
        setStatus({
          type: 'success',
          message: `Thank you! We'll notify you as soon as we announce details for ${cityName}.`
        });
        setIsSubmitSuccess(true);
        
        // Reset form after successful submission
        setFormData({
          firstName: '',
          lastName: '',
          email: '',
          subject: `Book Tour Notification - ${cityName}`,
          message: '',
          receiveUpdates: false
        });
        
        // Reset success state after delay
        setTimeout(() => {
          setStatus({ type: '', message: '' });
          setIsSubmitSuccess(false);
        }, 5000);
      } else {
        // Track failed form submission
        try {
          ReactGA.event('form_error', {
            event_category: 'error',
            item_name: 'city_notification_form_error',
            custom_parameters: {
              city: cityName
            }
          });
        } catch (trackingError) {
          console.warn('Analytics tracking failed:', trackingError);
        }
        
        setStatus({
          type: 'error',
          message: data.error || 'Something went wrong. Please try again.'
        });
      }
    } catch (error) {
      setStatus({
        type: 'error',
        message: 'Network error. Please check your connection and try again.'
      });
    } finally {
      setLoading(false);
    }
  };
  
  // Determine if form should be disabled
  const isFormDisabled = loading || isSubmitSuccess;
  
  return (
    <Paper sx={{ p: 3, height: 'fit-content' }}>
      <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
        <NotificationsIcon sx={{ mr: 1, color: 'secondary.main' }} />
        <Typography 
          variant="h6" 
          className={classes.formHeading}
          sx={{ mb: 0, color: 'secondary.main', fontWeight: 'bold', textAlign: 'left' }}
        >
          Get Notified
        </Typography>
      </Box>
      
      <Typography variant="body1" sx={{ mb: 3 }}>
        Be the first to know when we announce the date and venue for {cityName}!
      </Typography>

      {/* Success/Error Status Alert */}
      {status.message && (
        <Fade in={!!status.message}>
          <Alert 
            severity={status.type} 
            sx={{ mb: 3 }}
            icon={status.type === 'success' ? <CheckCircleIcon /> : undefined}
          >
            {status.message}
          </Alert>
        </Fade>
      )}

      {/* Form */}
      <Box 
        component="form" 
        onSubmit={handleSubmit}
        className={classes.form}
        sx={{
          opacity: isSubmitSuccess ? 0.3 : 1,
          pointerEvents: isSubmitSuccess ? 'none' : 'auto',
          transition: 'opacity 0.3s ease',
          mt: 0
        }}
      >
        <Box sx={{ display: 'flex', gap: 2, mb: 2 }}>
          <div className={classes.formField} style={{ flex: 1 }}>
            <TextField
              name="firstName"
              label="First Name"
              variant="outlined"
              fullWidth
              required
              value={formData.firstName}
              onChange={handleChange}
              disabled={isFormDisabled}
            />
          </div>
          <div className={classes.formField} style={{ flex: 1 }}>
            <TextField
              name="lastName"
              label="Last Name"
              variant="outlined"
              fullWidth
              required
              value={formData.lastName}
              onChange={handleChange}
              disabled={isFormDisabled}
            />
          </div>
        </Box>
        
        <div className={classes.formField}>
          <TextField
            name="email"
            label="Email Address"
            type="email"
            variant="outlined"
            fullWidth
            required
            value={formData.email}
            onChange={handleChange}
            disabled={isFormDisabled}
          />
        </div>

        <div className={classes.formField}>
          <TextField
            name="message"
            label="Suggested Location (Optional)"
            multiline
            rows={4}
            variant="outlined"
            fullWidth
            value={formData.message}
            onChange={handleChange}
            disabled={isFormDisabled}
            placeholder="Have a bookstore or venue suggestion? Let us know!"
          />
        </div>

        {/* Optional opt-in checkbox */}
        <div className={classes.optInContainer}>
          <label className={classes.optInLabel}>
            <input
              type="checkbox"
              name="receiveUpdates"
              checked={formData.receiveUpdates}
              onChange={handleChange}
              className={classes.optInCheckbox}
              disabled={isFormDisabled}
            />
            <span 
              className={classes.optInText}
              style={{ opacity: isFormDisabled ? 0.5 : 1 }}
            >
              I'd also like to receive updates about new blog posts and content (optional)
            </span>
          </label>
        </div>

        <Button
          type="submit"
          variant="contained"
          fullWidth
          disabled={isFormDisabled}
          className={classes.submitButton}
          sx={{
            py: 1.5,
            mt: 2,
            opacity: isFormDisabled ? 0.5 : 1
          }}
        >
          {loading ? (
            <CircularProgress size={24} color="inherit" />
          ) : isSubmitSuccess ? (
            'Notification Requested!'
          ) : (
            'Notify Me'
          )}
        </Button>
      </Box>
      
      {/* Success state overlay - shows when form is submitted successfully */}
      {isSubmitSuccess && (
        <Fade in={isSubmitSuccess}>
          <Box
            sx={{
              position: 'absolute',
              top: '50%',
              left: '50%',
              transform: 'translate(-50%, -50%)',
              textAlign: 'center',
              zIndex: 10
            }}
          >
            <CheckCircleIcon 
              sx={{ 
                fontSize: 48, 
                color: 'success.main', 
                mb: 1 
              }} 
            />
            <Typography variant="h6" sx={{ fontWeight: 'bold' }}>
              Notification Requested!
            </Typography>
            <Typography variant="body2" sx={{ mt: 1, opacity: 0.7 }}>
              We'll notify you as soon as details are available.
            </Typography>
          </Box>
        </Fade>
      )}
    </Paper>
  );
};

export default CityNotificationForm;